import { useState, useEffect } from "react";
import { Outlet, useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { getDeviceOrientation } from "../../utils/generalUtils";
import MainContainer from "../console/layout/MainContainer";
import NavigationMenu from "../console/components/NavigationMenu";
import ProfileBackground from "../../assets/images/backgrounds/UC_BG_Console.png";
import SelectorIntraverseLogoIcon from "../../assets/images/logos/HeroLogoIntraverse.png";
import { setColor, setFont } from "../../assets/styles/styles";
import Button from "../../components/common/button/Button";
import Footer from "../../components/layout/footer/Footer";
import { sendVerificationEmail, UpdateUserProfileData, Logout } from "../../api/UserController";
import { countryOptions } from "../../constants";
import Banner from "../../components/layout/ui/Banner";
import { UpdateUserRole } from "../../redux/actions/UserActions";
import PasswordChecker from "../login/PasswordChecker";
import LoadingIcon from "../../components/common/loading/LoadingIcon";

const VerifyEmail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user);
    const { paramToken, paramUserId } = useParams();
    const [orientation, setOrientation] = useState(getDeviceOrientation());
    const [redirectToNextStep, setRedirectToNextStep] = useState(false);
    const [country, setCountry] = useState(user.country || "");
    const [firstName, setFirstName] = useState(user.firstName || "");
    const [lastName, setLastName] = useState(user.lastName || "");
    const [successMessageBanner, setSuccessMessageBanner] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (paramToken === user.token && paramUserId === user.userId) {
            setRedirectToNextStep(true);
        }
    }, [paramToken, paramUserId]);

    const callSendVerificationEmail = async () => {
        const resp = await sendVerificationEmail(user.userId, user.token);
        if (resp && resp.status === 200) {
            setSuccessMessageBanner("Verification email resent! Please check your inbox.");
        } else {
            setSuccessMessageBanner("Error resending verification email. Please try again later.");
        }
    };

    const handleCompleteAccount = async () => {
        const updatedFields = {
            firstName,
            lastName,
            userCountry: country,
            password,
        };
        setLoading(true);
        const resp = await UpdateUserProfileData(updatedFields);
        if (resp && resp.status === 200) {
            dispatch(UpdateUserRole("user"));
            setTimeout(() => {
                navigate("/");
                setLoading(false);
            }, 2000);
        }
        if (!resp || resp.status !== 200) {
            navigate("/");
        }
    };

    const handleNewClick = async () => {
        await Logout();
        navigate("/sign-up");
    };

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="1000"
                message={`${successMessageBanner}`}
                type={"SUCCESS"}
                openBanner={!!successMessageBanner}
                setOpenBanner={setSuccessMessageBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                timer={8000}
                top={"78px"}
                left={"0"}
            />

            {loading && (
                <LoadingDiv>
                    <LoadingIcon />
                </LoadingDiv>
            )}

            {!loading && (
                <ConsoleContainer background={ProfileBackground}>
                    <MainContainer>
                        <Outlet />
                        <ContentContainer>
                            <StyledImage src={SelectorIntraverseLogoIcon} alt="Intraverse Logo" />
                            {!redirectToNextStep && (
                                <>
                                    <Title>
                                        Please check your email to verify your email address and continue creating your
                                        account!
                                    </Title>
                                    <BoldTitle>Verification sent to</BoldTitle>
                                    <InputBox type="text" id="email" value={user.email} disabled />
                                    <Button
                                        margin={"20px 0px 10px 0px"}
                                        label="Resend Email"
                                        variant="PurpleButton"
                                        buttonWidth="75%"
                                        onClick={callSendVerificationEmail}
                                    />
                                    <Title>
                                        Prefer to use a different email?{" "}
                                        <StyledA onClick={handleNewClick}>Create an account.</StyledA>
                                    </Title>
                                </>
                            )}

                            {redirectToNextStep && (
                                <>
                                    <Title>
                                        Email verified! Next, share more about yourself! Providing these details will
                                        personalize your experience even more.
                                    </Title>
                                    <Label>First Name</Label>
                                    <InputBox
                                        type="text"
                                        id="first name"
                                        placeholder="Enter First Name"
                                        value={user.firstName}
                                        onChange={(e) => setFirstName(e.currentTarget.value)}
                                    />

                                    <Label>Last Name</Label>
                                    <InputBox
                                        type="text"
                                        id="last name"
                                        placeholder="Enter Last Name"
                                        value={user.lastName}
                                        onChange={(e) => setLastName(e.currentTarget.value)}
                                    />

                                    <Label>Country</Label>
                                    <CountrySelect value={country} onChange={(e) => setCountry(e.currentTarget.value)}>
                                        <StyledOption value="" disabled hidden>
                                            Select a country
                                        </StyledOption>
                                        {countryOptions.map((country) => (
                                            <StyledOption key={country} value={country}>
                                                <p style={{ margin: "5px", width: "100px" }}> {country}</p>
                                            </StyledOption>
                                        ))}
                                    </CountrySelect>

                                    <Label>New Password</Label>
                                    <InputBox
                                        type="password"
                                        id="password"
                                        placeholder="What's your password?"
                                        value={password}
                                        onChange={(e) => setPassword(e.currentTarget.value)}
                                    />

                                    <PasswordChecker password={password} />

                                    <Button
                                        margin={"30px 0px 0px 0px"}
                                        label="Complete Account"
                                        variant="PurpleButton"
                                        buttonWidth="100%"
                                        onClick={handleCompleteAccount}
                                    />
                                </>
                            )}
                        </ContentContainer>
                    </MainContainer>
                    {!redirectToNextStep && <Footer />}
                </ConsoleContainer>
            )}
        </>
    );
};

export default VerifyEmail;

const ConsoleContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
    width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
    background: url(${(props) => props.background});
    background-size: cover;
    background-color: #1b1b23;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 50%;
    margin: 0rem auto;
    border-radius: 10px;

    @media (max-width: 1024px) {
        width: 75%;
    }

    @media (max-width: 768px) {
        width: 85%;
    }

    @media (max-width: 480px) {
        width: 95%;
        padding: 1.5rem;
    }
`;

const StyledImage = styled.img`
    width: 60%;
    max-width: 400px;
    height: auto;

    @media (max-width: 768px) {
        width: 80%;
    }
`;

const Title = styled.div`
    width: 100%;
    color: ${setColor.circularProgress};
    text-align: center;
    font-family: ${setFont.barlow};
    font-size: 16px;
    text-transform: none;
    letter-spacing: normal;
`;

const BoldTitle = styled.p`
    color: ${setColor.circularProgress};
    text-align: center;
    font-family: ${setFont.barlow};
    font-size: 20px;
    font-weight: 700;
    margin: 1.5rem 0 0.5rem 0;
    text-transform: uppercase;
`;

const InputBox = styled.input`
    width: 100%;
    height: 45px;
    padding: 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    background-color: #00000066;
    color: #ffffff;
    font-size: 16px;
`;

const StyledA = styled.a`
    color: ${setColor.accent4};
    text-decoration: none;
    cursor: pointer;
`;

const Label = styled.label`
    width: 100%;
    font-weight: 700;
    font-size: 16px;
    color: ${setColor.circularProgress};
    margin: 1rem 0 0.3rem 0;
`;

const CountrySelect = styled.select`
    width: 100%;
    height: 45px;
    padding: 10px 40px 10px 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    background-color: #00000066;
    color: #ffffff;
    font-size: 16px;
    font-family: ${setFont.barlow};
    outline: none;
    cursor: pointer;

    /* Force custom arrow styling */
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg fill='white' height='16' viewBox='0 0 20 20' width='16' xmlns='http://www.w3.org/2000/svg'><path d='M5.516 7.547l4.484 4.484 4.484-4.484L16 9.063l-6 6-6-6z'/></svg>");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 14px;

    &:focus {
        border-color: ${setColor.accent4};
    }
`;

const StyledOption = styled.option`
    background-color: red !important;
    color: #ffffff;
    font-family: ${setFont.barlow};
    font-size: 16px;
`;
const LoadingDiv = styled.div`
    position: fixed;
    inset: 0; /* top:0; right:0; bottom:0; left:0 */
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.4); /* optional overlay */
    z-index: 9999;
`;
